import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  Target,
  ArrowUpRight,
  ArrowLeft,
  Calculator,
} from "lucide-react";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateSIP, formatCurrency } from "../../utils/calculations";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import ProductLayout from "@/common/components/ProductLayout";
import { Button } from "@/common/components/ui/button";

export const SIPCalculator = () => {
  const navigate = useNavigate();
  const [sipAmount, setSipAmount] = useState(5000);
  const [sipRate, setSipRate] = useState(12);
  const [sipYears, setSipYears] = useState(5);

  const sipResult = useMemo(
    () => calculateSIP(sipAmount, sipRate, sipYears),
    [sipAmount, sipRate, sipYears]
  );

  const generateSIPChartData = () => {
    const data = [];
    const monthlyRate = Math.pow(1 + sipRate / 100, 1 / 12) - 1;
    for (let year = 1; year <= sipYears; year++) {
      const months = year * 12;
      const fv =
        sipAmount *
        ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
        (1 + monthlyRate);
      const invested = sipAmount * months;
      data.push({
        year: `Year ${year}`,
        invested: Math.round(invested),
        value: Math.round(fv),
      });
    }
    return data;
  };

  const sipPieData = [
    { name: "Invested", value: sipResult.invested, color: "#14b8a6" },
    { name: "Returns", value: sipResult.returns, color: "#f59e0b" },
  ];

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                SIP Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-8 flex items-center justify-between">
                    <div className="flex rounded-2xl bg-muted/50 p-1">
                      <button
                        onClick={() => navigate("/calculators/sip")}
                        className="rounded-xl bg-white px-6 py-2 text-sm font-black text-primary shadow-sm transition-all"
                      >
                        SIP
                      </button>
                      <button
                        onClick={() => navigate("/calculators/lumpsum")}
                        className="rounded-xl px-6 py-2 text-sm font-black text-slate-500 transition-all hover:text-slate-700"
                      >
                        Lumpsum
                      </button>
                    </div>
                    <div className="rounded-xl bg-amber-100 p-3">
                      <TrendingUp className="h-6 w-6 text-amber-600" />
                    </div>
                  </div>

                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label="Monthly investment"
                      value={sipAmount}
                      onChange={setSipAmount}
                      min={500}
                      max={10000000}
                      step={500}
                      prefix="₹"
                      limitLabels={["₹500", "₹1 Cr"]}
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="Expected return rate (p.a)"
                      value={sipRate}
                      onChange={setSipRate}
                      min={1}
                      max={30}
                      step={0.1}
                      suffix="%"
                      colorClass="text-green-600"
                    />

                    <CompactCalculatorRow
                      label="Time period"
                      value={sipYears}
                      onChange={setSipYears}
                      min={1}
                      max={40}
                      step={1}
                      suffix="Yrs"
                      limitLabels={["1 year", "40 years"]}
                      colorClass="text-purple-600"
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card className="border-border bg-muted/20">
                  <CardContent className="p-6 md:p-8">
                    <h3 className="mb-6 flex items-center gap-2 text-lg font-bold text-secondary">
                      <Target className="h-5 w-5 text-primary" />
                      Investment Summary
                    </h3>

                    <div className="mb-8 grid grid-cols-2 gap-6">
                      <div className="h-[220px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={sipPieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={90}
                              paddingAngle={0}
                              dataKey="value"
                            >
                              {sipPieData.map(entry => (
                                <Cell
                                  key={entry.name}
                                  fill={entry.color}
                                  stroke="none"
                                />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value: number | undefined) =>
                                value !== undefined ? formatCurrency(value) : ""
                              }
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex flex-col justify-center space-y-4">
                        {sipPieData.map(item => (
                          <div
                            key={item.name}
                            className="flex items-center gap-3"
                          >
                            <div
                              className="h-4 w-4 rounded-full"
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="text-sm font-bold text-slate-500">
                              {item.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mb-8 h-[150px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={generateSIPChartData()}>
                          <defs>
                            <linearGradient
                              id="colorValue"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#14b8a6"
                                stopOpacity={0.8}
                              />
                              <stop
                                offset="95%"
                                stopColor="#14b8a6"
                                stopOpacity={0.2}
                              />
                            </linearGradient>
                            <linearGradient
                              id="colorInvested"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#f59e0b"
                                stopOpacity={0.8}
                              />
                              <stop
                                offset="95%"
                                stopColor="#f59e0b"
                                stopOpacity={0.2}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e5e7eb"
                            vertical={false}
                          />
                          <XAxis
                            dataKey="year"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: "#6b7280", fontSize: 11 }}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: "#6b7280", fontSize: 11 }}
                            tickFormatter={v => `₹${(v / 100000).toFixed(0)}L`}
                          />
                          <Tooltip
                            formatter={(value: number | undefined) =>
                              value !== undefined ? formatCurrency(value) : ""
                            }
                          />
                          <Area
                            type="monotone"
                            dataKey="invested"
                            name="Invested"
                            stroke="#f59e0b"
                            fillOpacity={1}
                            fill="url(#colorInvested)"
                            strokeWidth={2}
                          />
                          <Area
                            type="monotone"
                            dataKey="value"
                            name="Value"
                            stroke="#14b8a6"
                            fillOpacity={1}
                            fill="url(#colorValue)"
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between rounded-xl bg-white p-4 shadow-sm">
                        <span className="text-muted-foreground">
                          Total Invested
                        </span>
                        <span className="text-xl font-bold text-secondary">
                          {formatCurrency(sipResult.invested)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between rounded-xl bg-white p-4 shadow-sm">
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <ArrowUpRight className="h-4 w-4 text-green-500" />
                          Estimated Returns
                        </span>
                        <span className="text-xl font-bold text-green-600">
                          {formatCurrency(sipResult.returns)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 p-5 text-white shadow-lg">
                        <span className="font-semibold">Total Value</span>
                        <span className="text-2xl font-bold">
                          {formatCurrency(sipResult.futureValue)}
                        </span>
                      </div>
                      <button className="mt-6 w-full rounded-xl bg-[#00d09c] py-4 font-black text-white shadow-xl shadow-[#00d09c]/20 transition-all hover:bg-[#00b88a] active:scale-[0.98]">
                        INVEST NOW
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
